import { ApolloLink, DocumentNode, NextLink, Operation } from '@apollo/client';
import {
  getMainDefinition,
  isQueryOperation,
  removeDirectivesFromDocument,
} from '@apollo/client/utilities';
import { EntityName } from '@provision/entity-types';
import { Kind } from 'graphql';
import { queryRegistry } from './query-registry';

const ENTITY_NAME_DIRECTIVE = {
  name: 'entity',
  valueSource: 'name',
};

export const queryRegistryLink = new ApolloLink((operation: Operation, forward: NextLink) => {
  if (isQueryOperation(operation.query)) {
    const entityName = getEntityName(operation.query);

    // do nothing if entityName does not exist as it's a regular case
    // since we have a bunch of queries that don't return entities, like
    // getAiHelp, getConversationAggregations etc.
    if (entityName) {
      queryRegistry.register({
        entityName,
        query: operation.query,
        variables: operation.variables,
        operationName: operation.operationName,
      });
    }
  }

  // we need to remove the directive from the query so it never reaches the server
  // as appSync doesn’t support arbitrary custom directives and will reject any of them
  operation.query = removeDirectivesFromDocument(
    [{ name: ENTITY_NAME_DIRECTIVE.name }],
    operation.query,
  )!;

  return forward(operation);
});

function getEntityName(query: DocumentNode) {
  // try to get entity name from query directives
  const def = getMainDefinition(query);
  const entityDir = def.directives?.find((d) => d.name.value === ENTITY_NAME_DIRECTIVE.name);
  const arg = entityDir?.arguments?.find(
    (a) => a.name.value === ENTITY_NAME_DIRECTIVE.valueSource,
  );

  // found it, returning
  if (arg?.value?.kind === Kind.STRING && arg.value.value) {
    return arg.value.value as EntityName;
  }

  return '';
}
